import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, useOutletContext } from 'react-router-dom'
import type { BrandColorCandidate, Project, FeedbackProgressEvent, Settings } from '@shared/types'
import { useLicense } from '../contexts/LicenseContext'
import { useApi } from '../contexts/ApiContext'
import { useGenerationContext } from '../contexts/GenerationContext'
import { ClientBrandingSchema, formatDashboardTitle, formatLocalTimestamp, getDashboardBaseName, getTotalCurrentFtes } from '@tn/shared'
import { deriveBrandingVariants, extractLogoPalette } from '../utils/branding'
import LogoUpload from '../components/LogoUpload'
import BrandLookupButton from '../components/BrandLookupButton'
import BrandPreviewHeader from '../components/BrandPreviewHeader'
import { resolveSessionIdentity } from '../utils/sessionIdentity'

export default function Dashboard() {
  const api = useApi()
  // Desktop uses a custom app:// protocol + <webview>; web uses an iframe URL.
  const isElectron = api.platform === 'desktop'
  const { projectId } = useParams<{ projectId: string }>()
  const [project, setProject] = useState<Project | null>(null)
  const [htmlContent, setHtmlContent] = useState<string>('')
  const [iframeUrl, setIframeUrl] = useState<string>('')
  // null = not yet resolved. Distinguishing this from '' (resolved, no preload available)
  // lets the webview wait for resolution instead of mounting prematurely — see the
  // getWebviewPreloadUrl effect below.
  const [webviewPreloadUrl, setWebviewPreloadUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  // Feedback panel state
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false)
  const [feedbackMode, setFeedbackMode] = useState<'add' | 'context' | 'update'>('update')
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackTarget, setFeedbackTarget] = useState<string>('')
  const [feedbackApplying, setFeedbackApplying] = useState(false)
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackProgressEvent | null>(null)
  const [feedbackError, setFeedbackError] = useState<string | null>(null)
  const [feedbackDone, setFeedbackDone] = useState(false)
  const [feedbackSkipped, setFeedbackSkipped] = useState<string | null>(null)
  const lastFeedbackProgressRef = useRef<FeedbackProgressEvent | null>(null)

  // Tab 4 regen state
  const [regenRunning, setRegenRunning] = useState(false)
  const [regenStatus, setRegenStatus] = useState<string | null>(null)
  const [regenError, setRegenError] = useState<string | null>(null)

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordExportTarget, setPasswordExportTarget] = useState<'html' | 'pdf'>('html')

  // Admin + share modal state
  const [isAdmin, setIsAdmin] = useState(false)
  const [sessionEmail, setSessionEmail] = useState('')
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [shareError, setShareError] = useState('')
  const [shareSuggestions, setShareSuggestions] = useState<string[]>([])

  // Combined "Edit Dashboard" modal (name + logo). Edits are held locally and
  // only committed to storage when the user clicks Save (deferred, dirty-gated —
  // mirrors the old rename modal's behavior for both fields).
  const [editingDashboard, setEditingDashboard] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [editName, setEditName] = useState('')
  const [editLogo, setEditLogo] = useState<string | undefined>(undefined)
  const [editVersion, setEditVersion] = useState('')
  const [editPrimaryColor, setEditPrimaryColor] = useState('#A100FF')
  const [editColorHexInput, setEditColorHexInput] = useState('#A100FF')
  const [editAccentColor, setEditAccentColor] = useState<string | undefined>(undefined)
  const [editAccentHexInput, setEditAccentHexInput] = useState('')
  // ── LLM-assisted brand color lookup + logo color opt-in, mirroring Step1/InsideOut
  // (plans/v3-shell-brand-color-palette-plan.md §4.1/§4.3) — same feature, applied to an
  // already-generated project instead of a fresh intake form. ──
  const [aiSettings, setAiSettings] = useState<Settings | null>(null)
  const [editLookupStatus, setEditLookupStatus] = useState<'idle' | 'loading' | 'found' | 'not-found' | 'error'>('idle')
  const [editLookupMessage, setEditLookupMessage] = useState<string | null>(null)
  const [editOtherCandidates, setEditOtherCandidates] = useState<BrandColorCandidate[]>([])
  const [editLogoColorCandidate, setEditLogoColorCandidate] = useState<string | null>(null)
  const [editColorSource, setEditColorSource] = useState<'brand' | 'logo'>('brand')
  const [editSavedBrandColor, setEditSavedBrandColor] = useState<string | null>(null)
  const editLogoRequestRef = useRef(0)
  const originalNameRef = useRef('')
  const originalLogoRef = useRef<string | undefined>(undefined)
  const originalVersionRef = useRef('')
  const originalPrimaryColorRef = useRef('')
  const originalAccentColorRef = useRef<string | undefined>(undefined)
  const editInputRef = useRef<HTMLInputElement>(null)

  const webviewRef = useRef<HTMLWebViewElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const navigate = useNavigate()

  const { refreshProjects } = useOutletContext<{ refreshProjects: () => Promise<void> }>()
  const { status: licenseStatus } = useLicense()
  const { activeJobs, trackJob, removeJob, feedbackCompletedProjects, clearFeedbackCompleted } = useGenerationContext()
  const isGeneratingThisProject = activeJobs.some(
    (j) => j.projectId === projectId && j.type !== 'feedback' && (j.status === 'pending' || j.status === 'running')
  )
  const activeFeedbackJob = activeJobs.find(
    (j) => j.projectId === projectId && j.type === 'feedback' && (j.status === 'pending' || j.status === 'running')
  )

  const canExport =
    licenseStatus?.isValid === true &&
    (licenseStatus.license?.features.includes('export') ?? false)
  const canRequestChanges = licenseStatus?.isValid === true

  const exportDisabledTooltip = !canExport
    ? licenseStatus?.isExpired
      ? 'License has expired'
      : !(licenseStatus?.license?.features.includes('export'))
        ? 'Export is not included in your license'
        : 'A valid license is required'
    : undefined

  const requestChangesDisabledTooltip = !canRequestChanges
    ? licenseStatus?.isExpired
      ? 'License has expired'
      : 'A valid license is required'
    : undefined

  useEffect(() => {
    void resolveSessionIdentity(api).then(({ isAdmin, email }) => { setIsAdmin(isAdmin); setSessionEmail(email) })
  }, [api])

  useEffect(() => {
    let mounted = true
    // Only feeds the edit modal's optional lookup button — a failure here just leaves
    // that button disabled (aiSettings stays null) rather than surfacing anywhere.
    api.getSettings().then((s) => { if (mounted) setAiSettings(s) }).catch(() => {})
    return () => { mounted = false }
  }, [api])

  // Recomputed whenever the edit modal's logo changes (including a fresh upload) —
  // mirrors Step1/InsideOut's extraction effect (plan §4.3).
  useEffect(() => {
    const requestId = ++editLogoRequestRef.current
    if (!editLogo) {
      setEditLogoColorCandidate(null)
      // Removing the logo also removes the "Use logo color" option — if that was the
      // active selection, revert the field to the saved brand color rather than leaving
      // it stranded on a now-orphaned logo-derived value with no toggle left to undo it.
      if (editColorSource === 'logo') {
        const restored = editSavedBrandColor || editPrimaryColor
        setEditColorHexInput(restored)
        setEditPrimaryColor(restored)
      }
      setEditColorSource('brand')
      return
    }
    // A freshly uploaded logo always supersedes whatever candidate the previous one
    // produced — re-extracting (rather than just clearing) is what makes replacing a
    // logo surface "Use logo color" again with the new logo's color.
    extractLogoPalette(editLogo).then((candidate) => {
      if (requestId === editLogoRequestRef.current) setEditLogoColorCandidate(candidate)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on a logo change, not on every editColorSource/editSavedBrandColor/editPrimaryColor update
  }, [editLogo])

  // Only the owner may edit the dashboard (rename + logo). Desktop is single-user
  // (always owner); on web, mirror the server's `canRename` gate (injectDashboardData)
  // exactly: createdBy === sessionEmail. Gates the toolbar button below; the in-dashboard
  // iframe icon is gated server-side.
  const canRename = api.platform === 'desktop' || (!!sessionEmail && project?.createdBy === sessionEmail)

  // The iframe icon is only injected server-side for owners, so this handler is reached
  // only when the viewer is authorized — no extra guard needed here.
  const startEditDashboard = useCallback(() => {
    // "Talent Navigator" is a fixed, non-negotiable suffix (formatDashboardTitle
    // re-appends it everywhere the title is rendered) — edit only the base portion so
    // the live preview below matches what actually gets rendered after Save.
    const baseName = getDashboardBaseName(project?.name || project?.client || '')
    const primary = project?.branding.primaryColor || '#A100FF'
    originalNameRef.current = baseName
    originalLogoRef.current = project?.branding.logoDataUrl
    originalVersionRef.current = project?.branding.versionLabel || ''
    originalPrimaryColorRef.current = primary
    originalAccentColorRef.current = project?.branding.accentColor
    setEditName(baseName)
    setEditLogo(project?.branding.logoDataUrl)
    setEditVersion(project?.branding.versionLabel || '')
    setEditPrimaryColor(primary)
    setEditColorHexInput(primary)
    setEditAccentColor(project?.branding.accentColor)
    setEditAccentHexInput(project?.branding.accentColor || '')
    setEditLookupStatus('idle')
    setEditLookupMessage(null)
    setEditOtherCandidates([])
    setEditColorSource('brand')
    setEditSavedBrandColor(null)
    setEditError('')
    setEditingDashboard(true)
    setTimeout(() => editInputRef.current?.select(), 0)
  }, [project])

  const isEditDashboardDirty =
    editName.trim() !== originalNameRef.current.trim() ||
    editLogo !== originalLogoRef.current ||
    editVersion.trim() !== originalVersionRef.current.trim() ||
    editPrimaryColor !== originalPrimaryColorRef.current ||
    (editAccentColor || '') !== (originalAccentColorRef.current || '')

  // Recomputed from the in-progress edit, not project.branding, so the preview and the dark
  // pill-text variant below track the color pickers live — same helper Step1/InsideOut use.
  const editVariants = deriveBrandingVariants(editPrimaryColor)

  function applyEditCandidate(candidate: BrandColorCandidate) {
    setEditColorSource('brand')
    if (candidate.primaryColor) { setEditPrimaryColor(candidate.primaryColor); setEditColorHexInput(candidate.primaryColor) }
    // Found a primary but no distinct accent — leave accent unset rather than inventing
    // one, per §4.2: the existing primaryColorDark/Light auto-derive fallback takes over.
    setEditAccentColor(candidate.accentColor ?? undefined)
    setEditAccentHexInput(candidate.accentColor ?? '')
  }

  // Lookup is Claude-only (see brand-lookup.ts) — once settings are known, gate the button
  // instead of letting a non-Claude click silently resolve to the generic "not found" notice.
  const editLookupUnsupportedProvider = !!aiSettings && aiSettings.aiProvider !== 'claude'

  async function handleEditLookupBrandColor() {
    const companyName = editName.trim()
    if (!companyName || !aiSettings || aiSettings.aiProvider !== 'claude') return
    setEditLookupStatus('loading')
    setEditLookupMessage(null)
    setEditOtherCandidates([])
    const apiKey =
      aiSettings.aiProvider === 'claude' ? aiSettings.anthropicApiKey
      : aiSettings.aiProvider === 'azure-openai' ? aiSettings.azureApiKey
      : aiSettings.openaiApiKey
    try {
      const result = await api.lookupBrandColors(companyName, aiSettings.aiProvider ?? 'claude', apiKey ?? '')
      if (result.verified && result.candidates.length > 0) {
        const [top, ...rest] = result.candidates
        applyEditCandidate(top)
        setEditOtherCandidates(rest)
        setEditLookupStatus('found')
      } else {
        setEditLookupStatus('not-found')
        setEditLookupMessage(`No public data about color or theme of ${companyName}. Upload a logo or use the color palette below.`)
      }
    } catch (err) {
      setEditLookupStatus('error')
      setEditLookupMessage(err instanceof Error ? err.message : 'Could not check brand color right now — please try again.')
    }
  }

  const useEditLogoColor = () => {
    if (!editLogoColorCandidate) return
    setEditSavedBrandColor(editPrimaryColor)
    setEditColorHexInput(editLogoColorCandidate)
    setEditPrimaryColor(editLogoColorCandidate)
    setEditColorSource('logo')
  }

  const useEditBrandColor = () => {
    const restored = editSavedBrandColor || editPrimaryColor
    setEditColorHexInput(restored)
    setEditPrimaryColor(restored)
    setEditColorSource('brand')
  }

  const handleEditColorChange = (color: string) => {
    setEditColorSource('brand')
    setEditPrimaryColor(color)
  }

  // Lightweight preview subtitle for the edit modal — mirrors the real dashboard
  // header's "N Subfunctions • N FTEs • Transformation Analysis" line (template.ts,
  // injectDashboardData) using the same shared FTE total helper, without
  // replicating that function's full KPI computation (role-task counts, provenance
  // pill, etc.) just for a preview banner.
  const dashboardSubtitle = project?.data
    ? `${project.data.subfunctions.length} Subfunctions • ${getTotalCurrentFtes(project.data).toLocaleString('en-US')} FTEs • Transformation Analysis`
    : ''

  // Mirrors template.ts's WIP-pill timestamp merge: date only, appended to the version
  // label when the dashboard is also in draft. Falls back to the bare label on a missing
  // or unparseable timestamp, same as the live dashboard removing the merge instead of
  // showing "Invalid Date".
  const versionPillSourceTimestamp = project?.updatedAt || project?.createdAt
  const versionPillDate = versionPillSourceTimestamp && !Number.isNaN(new Date(versionPillSourceTimestamp).getTime())
    ? formatLocalTimestamp(versionPillSourceTimestamp).split(' - ')[0]
    : undefined
  const versionPillText = editVersion.trim() && project?.branding?.isWip && versionPillDate
    ? `${editVersion.trim()} · ${versionPillDate}`
    : editVersion.trim()

  // With no version label, a draft dashboard still gets a date-only pill instead of
  // losing its timestamp entirely (mirrors template.ts).
  const previewVersionPillText = versionPillText || (project?.branding?.isWip ? versionPillDate : undefined)

  // The web server injects the dashboard action only for owners. Verify the message
  // came from that iframe so ownership does not depend on a second client check.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'tn:edit-dashboard-request' && e.source === iframeRef.current?.contentWindow) {
        startEditDashboard()
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [startEditDashboard])

  // Desktop's <webview> is its own isolated guest page, not a child window of the
  // host — the dashboard HTML's postMessage can't reach the `message` listener
  // above the way it does inside a web <iframe>. A small preload script attached
  // to the webview re-forwards it over Electron's webview IPC channel instead.
  useEffect(() => {
    if (!isElectron) return
    if (!api.getWebviewPreloadUrl) { setWebviewPreloadUrl(''); return }
    // Falls back to "no preload" on rejection so the webview still mounts (just without
    // the in-page edit pencil) instead of leaving webviewPreloadUrl stuck at null forever.
    void api.getWebviewPreloadUrl().then(setWebviewPreloadUrl).catch(() => setWebviewPreloadUrl(''))
  }, [isElectron, api])

  useEffect(() => {
    if (!isElectron) return
    const webview = webviewRef.current
    if (!webview) return
    // @tn/ui stays Electron-type-free (platform-agnostic), so this reads the
    // `channel` property `electron`'s real IpcMessageEvent carries without
    // importing its types — equivalent to `Electron.IpcMessageEvent`.
    const handler = (e: Event) => {
      if ((e as unknown as { channel?: string }).channel === 'tn:edit-dashboard-request') startEditDashboard()
    }
    webview.addEventListener('ipc-message', handler)
    return () => webview.removeEventListener('ipc-message', handler)
    // webviewPreloadUrl must stay a dependency: the <webview> only mounts once it
    // resolves (see render below), so if it resolves after htmlContent, this effect
    // needs to re-run to find webviewRef.current no longer null and attach the listener.
  }, [isElectron, startEditDashboard, htmlContent, webviewPreloadUrl])

  async function loadDashboard(id: string) {
    setLoading(true)
    setError(null)

    try {
      const proj = await api.getProject(id) ?? await api.getAdminDashboard(id)
      if (!proj) {
        setError('Project not found')
        setLoading(false)
        return
      }
      setProject(proj)

      // Project exists but generation hasn't completed yet — don't load the template.
      // injectDashboardData with null data renders the HTML shell with all-zero metrics.
      if (!proj.data) {
        setLoading(false)
        return
      }

      if (isElectron) {
        const templatePath = await api.getTemplatePath()
        if (!templatePath) {
          setError(
            'Navigator template not found. Make sure the HTML template file is in the same folder as the app.'
          )
          setLoading(false)
          return
        }
        setHtmlContent(
          `app://dashboard/?project=${encodeURIComponent(id)}&template=${encodeURIComponent(templatePath)}`
        )
      } else {
        setIframeUrl(`/api/files/projects/${encodeURIComponent(id)}/dashboard-html`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }

    setLoading(false)
  }

  async function confirmEditDashboard() {
    const trimmed = editName.trim()
    if (!trimmed) {
      setEditError('Name cannot be empty')
      return
    }
    if (!project || !projectId || editSaving) return

    const branding = { ...project.branding }
    if (editLogo === undefined) {
      delete branding.logoDataUrl
    } else {
      branding.logoDataUrl = editLogo
    }
    const trimmedVersion = editVersion.trim()
    if (trimmedVersion === '') {
      delete branding.versionLabel
    } else {
      branding.versionLabel = trimmedVersion
    }
    // Only touch stored color fields when the practitioner actually changed them — mirrors
    // the name/logo/version fields above, and avoids silently "normalizing" an existing
    // project's dark/light variants through deriveBrandingVariants on an unrelated edit
    // (e.g. a rename) just because this modal now also renders color pickers.
    if (editPrimaryColor !== originalPrimaryColorRef.current) {
      branding.primaryColor = editPrimaryColor
      branding.primaryColorDark = editVariants.primaryColorDark
      branding.primaryColorLight = editVariants.primaryColorLight
    }
    if ((editAccentColor || '') !== (originalAccentColorRef.current || '')) {
      if (editAccentColor === undefined) {
        delete branding.accentColor
      } else {
        branding.accentColor = editAccentColor
      }
    }
    const parsedBranding = ClientBrandingSchema.safeParse(branding)
    if (!parsedBranding.success) {
      setEditError(parsedBranding.error.issues.map((issue) => issue.message).join(', '))
      return
    }

    setEditSaving(true)
    setEditError('')
    const nameChanged = trimmed !== originalNameRef.current.trim()
    // Store the full canonical title (with the "Talent Navigator" suffix
    // re-attached) — same form formatDashboardTitle produces everywhere else the
    // name is rendered, so what the user saves is exactly what the preview showed.
    const fullTitle = formatDashboardTitle(trimmed)
    // Only touch name/client (list/toolbar + dashboard HTML template) when the name
    // actually changed — a logo-only save must not overwrite `client` with the
    // current (unchanged) name when the two happen to differ already.
    // `precomputed` (inside-out stage-pipeline results) is dropped rather than
    // spread through: ProjectSchema only accepts `null` (clear it) or an absent key
    // (leave the stored value alone) on save — the live object shape this modal
    // would otherwise forward fails validation outright (WI 2430).
    const { precomputed: _precomputed, ...projectWithoutPrecomputed } = project
    const updated: Project = { ...projectWithoutPrecomputed, branding, ...(nameChanged ? { name: fullTitle, client: fullTitle } : {}) }
    try {
      await api.saveProject(updated)
      setProject(updated)
      setEditingDashboard(false)
      if (nameChanged) {
        window.dispatchEvent(new CustomEvent('tn:project-renamed', { detail: { id: projectId, name: fullTitle, client: fullTitle } }))
      }
      // Adopt the logo path's explicit cache-bust unconditionally — a logo change
      // requires it, and it's harmless for a name-only change.
      if (isElectron) {
        const templatePath = await api.getTemplatePath()
        if (templatePath) {
          setHtmlContent('')
          setTimeout(() => {
            setHtmlContent(
              `app://dashboard/?project=${encodeURIComponent(projectId)}&template=${encodeURIComponent(templatePath)}&t=${Date.now()}`
            )
          }, 100)
        }
      } else {
        setIframeUrl(`/api/files/projects/${encodeURIComponent(projectId)}/dashboard-html?t=${Date.now()}`)
      }
      void refreshProjects().catch(() => {})
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save changes')
    } finally {
      setEditSaving(false)
    }
  }

  function cancelEditDashboard() {
    setEditingDashboard(false)
    setEditError('')
  }

  useEffect(() => {
    if (!projectId) return
    loadDashboard(projectId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // Keep the ESO session alive (and reset the client inactivity timer) while
  // the dashboard is open. A user reading the dashboard without moving the
  // mouse for 10 min would otherwise be auto-logged out.
  //
  // 'tn:generation-keepalive' is caught by EsoAuthGate which (a) resets the
  // client-side 10-min timeout and (b) pings GET /auth/session to refresh the
  // server-side lastActivity. Web-only — Electron has no ESO session.
  useEffect(() => {
    if (api.platform !== 'web') return
    const id = setInterval(() => {
      window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))
    }, 3 * 60 * 1000)
    return () => clearInterval(id)
  }, [api.platform])

  // Real-time: when a feedback job for this project reaches 'succeeded' while the Dashboard
  // is mounted, reload content immediately and show the notification.
  // loadDashboard is intentionally omitted — it is redefined each render and including it
  // would cause an infinite loop. removeJob is stable (useCallback with empty deps).
  useEffect(() => {
    const completedFeedback = activeJobs.find(
      (j) => j.projectId === projectId && j.type === 'feedback' && j.status === 'succeeded'
    )
    if (!completedFeedback || !projectId) return
    removeJob(completedFeedback.jobId)
    setFeedbackDone(true)
    loadDashboard(projectId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobs, projectId, removeJob])

  // Mount / return: show "Changes applied" when navigating back to a dashboard
  // whose feedback job completed while the user was elsewhere. The signal persists
  // in GenerationContext until the user has seen the full 8-second toast.
  useEffect(() => {
    if (!projectId || !feedbackCompletedProjects.has(projectId)) return
    setFeedbackDone(true)
  }, [projectId, feedbackCompletedProjects])

  // Auto-dismiss "Changes applied" badge after 8 seconds — and only THEN clear the
  // GenerationContext signal, so navigating away before the timer fires keeps the
  // signal alive for the next visit.
  useEffect(() => {
    if (!feedbackDone || activeFeedbackJob) return
    const t = setTimeout(() => {
      setFeedbackDone(false)
      if (projectId) clearFeedbackCompleted(projectId)
    }, 8000)
    return () => clearTimeout(t)
  }, [feedbackDone, activeFeedbackJob, projectId, clearFeedbackCompleted])

  const submitFeedback = async () => {
    if (!feedbackText.trim() || !projectId || !project) return

    setFeedbackError(null)
    setFeedbackDone(false)
    setFeedbackSkipped(null)
    setFeedbackStatus(null)
    lastFeedbackProgressRef.current = null

    try {
      const settings = await api.getSettings()

      // Support multiple providers safely (handles older builds too).
      const provider = settings?.aiProvider
      const apiKey =
        provider === 'claude'
          ? settings?.anthropicApiKey
          : provider === 'azure-openai'
            ? settings?.azureApiKey
            : settings?.openaiApiKey

      if (!apiKey) {
        setFeedbackError('No API key configured. Go to Settings.')
        return
      }

      const feedbackPayload = {
        projectId,
        feedback: feedbackText.trim(),
        targetSubfunctionName: feedbackMode === 'add' ? null : feedbackTarget,
        clientName: (project.client || '').trim(),
        industry: (project.industry || '').trim() || 'General',
        provider,
        apiKey: String(apiKey).trim(),
        brandColor: project.branding?.primaryColor,
      }

      // Web: background job — enqueue and return immediately so the user can navigate away.
      if (api.startFeedbackJob) {
        setFeedbackApplying(true)
        const result = await api.startFeedbackJob(feedbackPayload)
        setFeedbackApplying(false)
        if (result.error || !result.jobId) {
          setFeedbackError(result.error ?? 'Failed to start feedback job')
          return
        }
        trackJob(result.jobId, projectId, 'feedback', (project.client || project.name || '').trim(), project.branding?.primaryColor || '#A100FF')
        setFeedbackText('')
        setShowFeedbackPanel(false)
        return
      }

      // Desktop: synchronous IPC path — block UI until complete.
      setFeedbackApplying(true)
      let unsub: () => void = () => {}
      const keepAlive = setInterval(() => {
        window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))
      }, 270_000)

      try {
        unsub = api.onFeedbackProgress((ev: FeedbackProgressEvent) => {
          lastFeedbackProgressRef.current = ev
          setFeedbackStatus(ev)
        })

        const result = await api.applyFeedback(feedbackPayload)

        if (result?.success) {
          const lastProg = lastFeedbackProgressRef.current as FeedbackProgressEvent | null
          if (lastProg?.status === 'error') {
            setFeedbackSkipped(lastProg.label)
          } else {
            setFeedbackDone(true)
            setFeedbackText('')

            const updated = await api.getProject(projectId)
            if (updated) setProject(updated)
            void refreshProjects().catch(() => {})

            if (isElectron) {
              const templatePath = await api.getTemplatePath()
              if (templatePath) {
                setHtmlContent('')
                setTimeout(() => {
                  setHtmlContent(
                    `app://dashboard/?project=${encodeURIComponent(projectId)}&template=${encodeURIComponent(templatePath)}&t=${Date.now()}`
                  )
                }, 100)
              }
            } else {
              setIframeUrl(`/api/files/projects/${encodeURIComponent(projectId)}/dashboard-html?t=${Date.now()}`)
            }
          }
        } else {
          setFeedbackError(result?.error || 'Unknown error')
        }
      } catch (err) {
        setFeedbackError(err instanceof Error ? err.message : String(err))
      } finally {
        clearInterval(keepAlive)
        try { unsub() } catch { /* ignore */ }
        setFeedbackApplying(false)
      }
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : String(err))
    }
  }

  const runRegenTab4 = async () => {
    if (!projectId || !project) return
    setRegenRunning(true)
    setRegenStatus('Starting…')
    setRegenError(null)

    let unsub: () => void = () => {}
    // Prevent inactivity logout during Tab-4 regeneration.
    const keepAlive = setInterval(() => {
      window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))
    }, 270_000) // 4.5 min — well within the 10-min inactivity TTL
    try {
      const settings = await api.getSettings()
      const provider = settings?.aiProvider
      const apiKey = provider === 'claude' ? settings?.anthropicApiKey
        : provider === 'azure-openai' ? settings?.azureApiKey
        : settings?.openaiApiKey

      if (!apiKey) { setRegenError('No API key configured. Go to Settings.'); return }

      unsub = api.onRegenTab4Progress((ev: FeedbackProgressEvent) => {
        setRegenStatus(`${ev.label}: ${ev.status}`)
      })

      const result = await api.regenTab4({ projectId, provider, apiKey: String(apiKey).trim() })

      if (result?.success) {
        setRegenStatus('Done — reloading…')
        const updated = await api.getProject(projectId)
        if (updated) setProject(updated)
        if (isElectron) {
          const templatePath = await api.getTemplatePath()
          if (templatePath) {
            setHtmlContent('')
            setTimeout(() => {
              setHtmlContent(`app://dashboard/?project=${encodeURIComponent(projectId)}&template=${encodeURIComponent(templatePath)}&t=${Date.now()}`)
            }, 100)
          }
        } else {
          setIframeUrl(`/api/files/projects/${encodeURIComponent(projectId)}/dashboard-html?t=${Date.now()}`)
        }
        setRegenStatus(null)
      } else {
        setRegenError(result?.error || 'Unknown error')
      }
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : String(err))
    } finally {
      clearInterval(keepAlive)
      try { unsub() } catch { /* ignore */ }
      setRegenRunning(false)
    }
  }


  const openPasswordModal = (target: 'html' | 'pdf') => {
    setPasswordExportTarget(target)
    setPasswordInput('')
    setPasswordConfirm('')
    setPasswordError('')
    setShowPasswordModal(true)
  }

  const confirmPasswordExport = async () => {
    if (!passwordInput) {
      setPasswordError('Please enter a password.')
      return
    }
    if (passwordInput !== passwordConfirm) {
      setPasswordError('Passwords do not match.')
      return
    }
    if (passwordInput.length < 4) {
      setPasswordError('Password must be at least 4 characters.')
      return
    }
    if (!projectId) return

    setShowPasswordModal(false)
    setExporting(true)

    try {
      if (passwordExportTarget === 'pdf') {
        await api.exportPdfProtected(projectId, passwordInput)
      } else {
        await api.exportHtmlProtected(projectId, passwordInput)
      }
    } catch {
      // intentionally ignored (existing behavior)
    }

    setExporting(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        Loading navigator…
      </div>
    )
  }

  if (project && !project.data) {
    if (isGeneratingThisProject) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8 animate-fade-in">
          <div className="text-5xl mb-2">⏳</div>
          <h2 className="text-xl font-semibold text-gray-800">Generation in Progress</h2>
          <p className="text-sm text-gray-500 max-w-sm">
            This navigator is still being generated. Go to the Home page to monitor progress.
          </p>
          <button onClick={() => navigate('/')} className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium underline">
            ← Back to Home
          </button>
        </div>
      )
    }
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8 animate-fade-in">
        <div className="text-5xl mb-2">⚠️</div>
        <h2 className="text-xl font-semibold text-gray-800">Navigator Not Available</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          The generation did not complete successfully. No navigator data is available for this project.
        </p>
        <button onClick={() => navigate('/')} className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium underline">
          ← Back to Home
        </button>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
        <div className="text-4xl">⚠️</div>
        <h2 className="text-lg font-semibold text-gray-800">Navigator Error</h2>
        <p className="text-sm text-gray-500 max-w-md">{error}</p>
        <button onClick={() => navigate('/')} className="text-sm text-purple-600 underline">
          Back to Home
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-white flex-shrink-0 gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-shrink">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            ← Back
          </button>
          {project?.sourceGccProjectId && api.platform === 'web' && (
            <>
              <span className="text-gray-200 flex-shrink-0">|</span>
              <button
                onClick={() => navigate(`/gcc/dashboard/${project.sourceGccProjectId}`)}
                className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors flex-shrink-0 flex items-center gap-1"
              >
                ← GCC Design
              </button>
            </>
          )}
          {project && (
            <>
              <span className="text-gray-200 flex-shrink-0">|</span>
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: project.branding?.primaryColor || '#A100FF' }}
                />
                <div className="flex items-center gap-1.5 min-w-0">
                    <h1 className="text-sm font-semibold text-gray-800 truncate">
                      {formatDashboardTitle(project.name || project.client)}
                    </h1>
                    {canRename && (
                      <button
                        onClick={startEditDashboard}
                        aria-label="Edit dashboard"
                        title="Edit dashboard"
                        className="text-gray-500 hover:text-purple-600 flex-shrink-0 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4">
                          <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.75 9.775a.75.75 0 0 0-.197.384l-.5 2.5a.75.75 0 0 0 .888.888l2.5-.5a.75.75 0 0 0 .384-.197l7.263-7.263a1.75 1.75 0 0 0 0-2.475Z" />
                        </svg>
                      </button>
                    )}
                  </div>
                {project.branding?.isWip && (
                  <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">
                    WIP
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          {activeFeedbackJob && (
            <span className="text-xs text-purple-600 flex items-center gap-1">
              <span className="animate-spin inline-block">⟳</span>
              {activeFeedbackJob.feedbackProgress?.find((e) => e.status === 'generating')?.label ?? 'Applying changes…'}
            </span>
          )}
          {feedbackDone && !activeFeedbackJob && (
            <span className="text-xs text-green-600">✓ Changes applied</span>
          )}
          {regenError && (
            <span className="text-xs text-red-500 max-w-xs truncate" title={regenError}>
              ✗ {regenError}
            </span>
          )}
          {regenStatus && !regenError && (
            <span className="text-xs text-gray-400">{regenStatus}</span>
          )}
          {project?.data && (
            <button
              onClick={runRegenTab4}
              disabled={regenRunning || !canRequestChanges}
              title="Re-run AI to generate new automation builds and process redesign"
              className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {regenRunning ? 'Regenerating…' : 'Regenerate AI & Process'}
            </button>
          )}
          <button
            onClick={() => openPasswordModal('html')}
            disabled={exporting || !canExport}
            title={exportDisabledTooltip}
            className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Download HTML
          </button>
          <button
            onClick={() => {
              setShowFeedbackPanel(true)
              setFeedbackDone(false)
              setFeedbackError(null)
              setFeedbackText('')
              setFeedbackTarget('')
              setFeedbackMode('update')
            }}
            disabled={!canRequestChanges}
            title={requestChangesDisabledTooltip ?? 'Request changes or additions to this navigator'}
            className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ✏️ Request Changes
          </button>
          <button
            onClick={() => {
              if (!projectId) return
              if (api.supportsPasswordExport) {
                openPasswordModal('pdf')
              } else {
                iframeRef.current?.contentWindow?.print()
              }
            }}
            disabled={exporting || !canExport}
            title={exportDisabledTooltip}
            className="text-[10px] font-semibold px-2 py-1 rounded-lg text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: project?.branding?.primaryColor || '#A100FF' }}
          >
            Export PDF
          </button>
          {isAdmin && <button
            onClick={() => { setShowShareModal(true); setShareStatus('idle'); setShareEmail(''); setShareError('') }}
            title="Share navigator"
            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>}
        </div>
      </div>

      {/* Edit Dashboard modal — combined rename + logo (Design 4: split layout + live preview) */}
      {editingDashboard && project && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={editSaving ? undefined : cancelEditDashboard}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-dashboard-title"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4">
              <h2 id="edit-dashboard-title" className="text-base font-semibold text-gray-900 mb-3">Edit Dashboard</h2>

              {/* Mirrors the real v3 dashboard header (template.ts, injectDashboardData) —
                  shared with Outside-In/Inside-Out so every brand-color preview matches
                  what actually ships. */}
              <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">Preview</p>
              <BrandPreviewHeader
                primaryColor={editPrimaryColor}
                accentColor={editAccentColor}
                logoUrl={editLogo}
                title={formatDashboardTitle(editName)}
                subtitle={dashboardSubtitle || undefined}
                versionPillText={previewVersionPillText}
                showDraftPill={!!project.branding?.isWip}
              />
            </div>

            <div className="grid grid-cols-5 gap-6 px-6 pb-2">
              {/* Left: logo dropzone (reuses LogoUpload, saves locally only until Save is clicked) */}
              <div className="col-span-2">
                <div className={editSaving ? 'pointer-events-none opacity-60' : ''}>
                  <LogoUpload value={editLogo} onChange={setEditLogo} />
                </div>
              </div>

              {/* Right: name/version, then brand + accent color (Design: compact 2-up rows
                  so adding brand color didn't double the modal's height). */}
              <div className="col-span-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label htmlFor="edit-dashboard-name" className="block text-xs font-medium text-gray-600 mb-1">Dashboard name</label>
                    <div className="flex items-stretch border border-gray-300 rounded-lg overflow-hidden focus-within:border-purple-400 focus-within:ring-1 focus-within:ring-purple-300">
                      <input
                        id="edit-dashboard-name"
                        ref={editInputRef}
                        type="text"
                        value={editName}
                        maxLength={100}
                        disabled={editSaving}
                        placeholder="Enter a client or project name"
                        className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-800 focus:outline-none disabled:opacity-50"
                        onChange={(e) => { setEditName(e.target.value); setEditError('') }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void confirmEditDashboard()
                          if (e.key === 'Escape' && !editSaving) cancelEditDashboard()
                        }}
                      />
                      <span className="flex-shrink-0 flex items-center px-3 text-sm text-gray-400 bg-gray-50 border-l border-gray-200 select-none">
                        Talent Navigator
                      </span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="edit-dashboard-version" className="block text-xs font-medium text-gray-600 mb-1">Dashboard version</label>
                    <input
                      id="edit-dashboard-version"
                      type="text"
                      value={editVersion}
                      maxLength={60}
                      disabled={editSaving}
                      placeholder="V1"
                      className="w-full px-3 py-2 text-sm text-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-300 focus:border-purple-400 disabled:opacity-50"
                      onChange={(e) => { setEditVersion(e.target.value); setEditError('') }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void confirmEditDashboard()
                        if (e.key === 'Escape' && !editSaving) cancelEditDashboard()
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1 mb-4">
                  <p className="text-xs text-gray-400">&quot;Talent Navigator&quot; is appended automatically. Version shows as a header pill.</p>
                  <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{editName.length}/100</span>
                </div>

                <div className="flex items-center justify-between mb-1.5">
                  <span className="block text-xs font-medium text-gray-600">Dashboard colors</span>
                  <BrandLookupButton
                    onClick={() => void handleEditLookupBrandColor()}
                    disabled={editSaving || !editName.trim() || editLookupStatus === 'loading' || editLookupUnsupportedProvider}
                    loading={editLookupStatus === 'loading'}
                    title={editLookupUnsupportedProvider ? 'Brand color lookup requires the Claude provider — change it in Settings.' : undefined}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="edit-dashboard-color" className="block text-[11px] text-gray-400 mb-1">Brand color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        aria-label="Dashboard brand color picker"
                        value={editPrimaryColor}
                        disabled={editSaving}
                        onChange={(e) => { handleEditColorChange(e.target.value); setEditColorHexInput(e.target.value) }}
                        className="w-9 h-9 flex-shrink-0 rounded-lg border border-gray-200 cursor-pointer p-0.5 disabled:opacity-50"
                      />
                      <input
                        id="edit-dashboard-color"
                        type="text"
                        value={editColorHexInput}
                        disabled={editSaving}
                        placeholder="#A100FF"
                        className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-800 border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-1 focus:ring-purple-300 focus:border-purple-400 disabled:opacity-50"
                        onChange={(e) => {
                          const val = e.target.value
                          setEditColorHexInput(val)
                          if (/^#[0-9A-Fa-f]{6}$/.test(val)) handleEditColorChange(val)
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="edit-dashboard-accent" className="block text-[11px] text-gray-400">Accent <span className="text-gray-300">(optional)</span></label>
                      {editAccentColor && (
                        <button
                          type="button"
                          disabled={editSaving}
                          onClick={() => { setEditAccentHexInput(''); setEditAccentColor(undefined) }}
                          className="text-[11px] text-gray-400 hover:text-gray-600 underline flex-shrink-0 disabled:opacity-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        aria-label="Dashboard accent color picker"
                        value={editAccentColor || editPrimaryColor}
                        disabled={editSaving}
                        onChange={(e) => { setEditAccentColor(e.target.value); setEditAccentHexInput(e.target.value) }}
                        className="w-9 h-9 flex-shrink-0 rounded-lg border border-gray-200 cursor-pointer p-0.5 disabled:opacity-50"
                      />
                      <input
                        id="edit-dashboard-accent"
                        type="text"
                        value={editAccentHexInput}
                        disabled={editSaving}
                        placeholder="Auto-derived"
                        className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-800 border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-1 focus:ring-purple-300 focus:border-purple-400 disabled:opacity-50"
                        onChange={(e) => {
                          const val = e.target.value
                          setEditAccentHexInput(val)
                          if (val === '') setEditAccentColor(undefined)
                          else if (/^#[0-9A-Fa-f]{6}$/.test(val)) setEditAccentColor(val)
                        }}
                      />
                    </div>
                  </div>
                </div>

                {editLookupStatus === 'not-found' && editLookupMessage && (
                  <p role="status" className="text-xs text-gray-500 mt-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    {editLookupMessage}
                  </p>
                )}
                {editLookupStatus === 'error' && editLookupMessage && (
                  <p role="alert" className="text-xs text-red-500 mt-2">
                    {editLookupMessage}
                  </p>
                )}
                {editLookupStatus === 'found' && (
                  <p className="text-xs text-purple-600 mt-2">
                    Updated from publicly available brand data — review and adjust if needed.
                  </p>
                )}

                {/* Other ranked candidates (plan §4.1) — the top one is auto-applied above. */}
                {editLookupStatus === 'found' && editOtherCandidates.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1.5">Other suggested colors for {editName}:</p>
                    <div className="flex items-center gap-2">
                      {editOtherCandidates.map((candidate, i) => (
                        <button
                          key={i}
                          type="button"
                          disabled={editSaving}
                          onClick={() => applyEditCandidate(candidate)}
                          title={candidate.primaryColor ?? undefined}
                          aria-label={`Use suggested color ${candidate.primaryColor ?? ''}`}
                          className="w-7 h-7 rounded-md border-2 border-gray-200 hover:border-purple-400 transition-all flex-shrink-0 disabled:opacity-50"
                          style={{ background: candidate.primaryColor ?? '#D1D5DB' }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Color source: each option only appears once its source has actually
                    produced a color this session — "Use brand color" needs a successful
                    lookup, "Use logo color" needs an extractable logo candidate. Neither,
                    one, or both can be true. */}
                {(editLookupStatus === 'found' || editLogoColorCandidate) && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-gray-500">Color source:</span>
                    {editLookupStatus === 'found' && (
                      <button
                        type="button"
                        disabled={editSaving}
                        onClick={useEditBrandColor}
                        className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all disabled:opacity-50 ${
                          editColorSource === 'brand' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        Use brand color
                      </button>
                    )}
                    {editLogoColorCandidate && (
                      <button
                        type="button"
                        disabled={editSaving}
                        onClick={useEditLogoColor}
                        className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all flex items-center gap-1.5 disabled:opacity-50 ${
                          editColorSource === 'logo' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <span className="inline-block w-3 h-3 rounded-full border border-gray-200" style={{ background: editLogoColorCandidate }} />
                        Use logo color
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {editError && <p role="alert" className="text-xs text-red-500 px-6 mb-3">{editError}</p>}

            <div className="flex gap-2 justify-end px-6 py-4 bg-gray-50 border-t mt-4">
              <button
                onClick={cancelEditDashboard}
                disabled={editSaving}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => void confirmEditDashboard()}
                disabled={editSaving || !editName.trim() || !isEditDashboardDirty}
                className="px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Share Navigator</h2>
              <button onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Enter the EID of the user you want to share <span className="font-medium text-gray-700">{project?.name || project?.client}</span> with.</p>
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="e.g. firstname.m.lastname"
                value={shareEmail}
                onChange={async (e) => {
                  const val = e.target.value.replace(/@.*$/, '')
                  setShareEmail(val)
                  setShareError('')
                  if (val.trim().length >= 1) {
                    const results = await api.searchUsers(val.trim())
                    setShareSuggestions(results.map((r) => r.email))
                  } else {
                    setShareSuggestions([])
                  }
                }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300"
                disabled={shareStatus === 'sending' || shareStatus === 'success'}
                autoComplete="off"
              />
              {shareSuggestions.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {shareSuggestions.map((email) => (
                    <li
                      key={email}
                      onClick={() => { setShareEmail(email); setShareSuggestions([]) }}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 cursor-pointer"
                    >
                      {email}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {shareError && <p className="text-xs text-red-500 mb-3">{shareError}</p>}
            {shareStatus === 'success' && (
              <p className="text-xs text-green-600 mb-3">Navigator shared successfully with {shareEmail.includes('@') ? shareEmail.split('@')[0] : shareEmail}.</p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowShareModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button
                disabled={shareStatus === 'sending' || shareStatus === 'success' || !shareEmail.trim()}
                onClick={async () => {
                  if (!shareEmail.trim() || !projectId) return
                  setShareStatus('sending')
                  try {
                    await api.shareProject(projectId, shareEmail.trim().toLowerCase())
                    setShareStatus('success')
                  } catch (err) {
                    setShareError(err instanceof Error ? err.message : 'Failed to share')
                    setShareStatus('error')
                  }
                }}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                style={{ background: project?.branding?.primaryColor || '#A100FF' }}
              >
                {shareStatus === 'sending' ? 'Sharing…' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-96">
            <div className="text-2xl mb-1">🔒</div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {passwordExportTarget === 'pdf' ? 'Export Protected PDF' : 'Download Protected HTML'}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {passwordExportTarget === 'pdf'
                ? 'Recipients will need this password to open the exported PDF.'
                : 'Recipients will be prompted to enter this password before viewing the navigator.'}
            </p>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value)
                  setPasswordError('')
                }}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300"
                placeholder="Enter a password"
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => {
                  setPasswordConfirm(e.target.value)
                  setPasswordError('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmPasswordExport()
                }}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300"
                placeholder="Confirm password"
              />
            </div>
            {passwordError && <p className="text-xs text-red-600 mb-3">{passwordError}</p>}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmPasswordExport}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
              >
                {passwordExportTarget === 'pdf' ? 'Export Protected PDF' : 'Download Protected HTML'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Changes panel */}
      {showFeedbackPanel && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="flex-1 bg-black/30"
            onClick={() => setShowFeedbackPanel(false)}
          />
          {/* Panel */}
          <div className="w-[420px] bg-white h-full flex flex-col shadow-2xl">
            <div
              className="px-5 py-4 flex items-center justify-between flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              <div>
                <div className="text-white font-bold text-base">Request Changes</div>
                <div className="text-purple-200 text-xs mt-0.5">
                  Talent Navigator will update the navigator based on your instructions
                </div>
              </div>
              <button
                onClick={() => setShowFeedbackPanel(false)}
                className="text-white/70 hover:text-white text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {/* Mode selector */}
              {!feedbackApplying && !feedbackDone && (
                <div className="mb-4 grid grid-cols-3 gap-2">
                  {(
                    [
                      { key: 'add', label: 'Add Subfunction', icon: '➕', desc: 'Add a missing team or function' },
                      { key: 'context', label: 'Provide Context', icon: '💡', desc: 'Share info to improve accuracy' },
                      { key: 'update', label: 'Request Update', icon: '✏️', desc: 'Edit roles, tasks, or data' },
                    ] as const
                  ).map((mode) => (
                    <button
                      key={mode.key}
                      onClick={() => {
                        setFeedbackMode(mode.key)
                        setFeedbackText('')
                      }}
                      className={`p-2.5 rounded-xl border-2 text-left transition-all ${
                        feedbackMode === mode.key
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="text-lg mb-0.5">{mode.icon}</div>
                      <div
                        className={`text-[11px] font-bold leading-tight ${
                          feedbackMode === mode.key ? 'text-purple-700' : 'text-gray-700'
                        }`}
                      >
                        {mode.label}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">{mode.desc}</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Subfunction selector (context + update modes only — optional) */}
              {feedbackMode !== 'add' && !feedbackApplying && !feedbackDone && (
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    {feedbackMode === 'context' ? 'Which area does this apply to?' : 'Which subfunction? '}
                    <span className="font-normal text-gray-400">(optional — leave blank to apply globally)</span>
                  </label>
                  <select
                    value={feedbackTarget}
                    onChange={(e) => setFeedbackTarget(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                  >
                    <option value="">— All subfunctions / entire navigator —</option>
                    {project?.data?.subfunctions?.map((sf) => (
                      <option key={sf.name} value={sf.name}>
                        {sf.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Instructions */}
              {!feedbackDone && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    {feedbackMode === 'add'
                      ? 'Describe the new subfunction'
                      : feedbackMode === 'context'
                        ? 'What should Talent Navigator know?'
                        : 'What would you like to change?'}
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    disabled={feedbackApplying}
                    rows={5}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
                    placeholder={
                      feedbackMode === 'add'
                        ? 'e.g. "Add a Data Governance subfunction with roles for Data Steward, Data Quality Analyst, and Metadata Manager"'
                        : feedbackMode === 'context'
                          ? 'e.g. "The client is heavily focused on RPA for back-office processes" or "This team works closely with external vendors — stakeholder management is critical" or "The client has a headcount freeze so no new roles should be added"'
                          : 'e.g. "Add a Senior Analyst role with 4 FTEs" or "Increase the GenAI task mix" or "Change the FTE count for HR Manager to 8"'
                    }
                  />
                </div>
              )}

              {/* Examples */}
              {!feedbackApplying && !feedbackDone && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                    Examples
                  </div>
                  {(feedbackMode === 'add'
                    ? [
                        'Add a Vendor Management subfunction',
                        'Add a Data Governance team with 3 roles',
                        'Include an Internal Communications function',
                      ]
                    : feedbackMode === 'context'
                      ? [
                          'The client is prioritizing Agentic AI this year',
                          'This team is undergoing a major restructure',
                          'The client has a headcount freeze — no new hires',
                          'Focus on cost reduction, not growth',
                        ]
                      : [
                          'Add a Team Lead role with 2 FTEs',
                          'Change the FTE count for this role to 6',
                          'Increase the Agentic AI task percentage',
                          'Remove the junior analyst role',
                        ]
                  ).map((ex) => (
                    <button
                      key={ex}
                      onClick={() => setFeedbackText(ex)}
                      className="block w-full text-left text-xs text-purple-700 hover:text-purple-900 py-0.5"
                    >
                      → {ex}
                    </button>
                  ))}
                </div>
              )}

              {/* Progress */}
              {feedbackApplying && feedbackStatus && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-purple-700">
                    <span className="animate-spin">⟳</span>
                    <span>{feedbackStatus.label}</span>
                  </div>
                </div>
              )}
              {feedbackApplying && !feedbackStatus && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700 flex items-center gap-2">
                  <span className="animate-spin">⟳</span> Applying changes…
                </div>
              )}

              {/* Skipped */}
              {feedbackSkipped && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  ⚠ {feedbackSkipped}
                </div>
              )}

              {/* Success */}
              {feedbackDone && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <div>✓ Navigator updated successfully. The view has been refreshed.</div>
                  <div className="mt-1.5 text-xs text-green-600">
                    Tab 3 &amp; 4 may be out of sync — use <span className="font-semibold">Regenerate AI &amp; Process</span> in the toolbar to update them.
                  </div>
                </div>
              )}

              {/* Error */}
              {feedbackError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  ✗ {feedbackError}
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-100 flex-shrink-0 flex gap-3">
              <button
                onClick={() => setShowFeedbackPanel(false)}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 disabled:opacity-40"
              >
                {feedbackApplying ? 'Close' : activeFeedbackJob ? 'Close (running in background)' : 'Close'}
              </button>
              <button
                onClick={submitFeedback}
                disabled={feedbackApplying || !!activeFeedbackJob || !feedbackText.trim()}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-all"
                style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
              >
                {feedbackApplying ? 'Applying…' : activeFeedbackJob ? 'Changes in Progress…' : 'Apply Changes →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard view — webview on Electron, iframe on web */}
      <div className="flex-1 overflow-hidden relative">
        {isElectron && htmlContent && webviewPreloadUrl !== null && (
          // Electron doesn't reliably re-inject `preload` into a guest page that has
          // already started navigating, so the element must not mount with `src` set
          // until the preload URL has resolved (even to '', meaning none available) —
          // otherwise the edit-dashboard icon's postMessage can silently never reach
          // the host on a run where this resolves after htmlContent does (WI 2430).
          <webview
            ref={webviewRef}
            src={htmlContent}
            preload={webviewPreloadUrl || undefined}
            className="w-full h-full border-0"
            style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
            webpreferences="contextIsolation=true"
          />
        )}
        {!isElectron && iframeUrl && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            className="w-full h-full border-0"
            title="Navigator"
          />
        )}
      </div>
    </div>
  )
}